import { Button } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { useTranslation } from "react-i18next";
import { GfFlyerItemComponentPosition } from "@swagger/typescript-fetch-goflyer";
import { FrontPage } from "../../../utils/constant/stringConstant";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { RenderDeleteButton } from "../../components/RenderDeleteButtonTable";

export const useFigmaComponentLayoutTableColumns = (
  onShowCoordinates?: (item: GfFlyerItemComponentPosition) => void,
  onEditTemplate?: (item: GfFlyerItemComponentPosition) => void,
  handleOnDelete?: (item: GfFlyerItemComponentPosition) => void
) => {
  const { t } = useTranslation();
  const values: AllowedTableColumn[] = ["updatedAt", "createdAt"];
  const showDeleteButton =
    typeof process.env.REACT_APP_SHOW_COMPONENT_LAYOUT_DELETE_BUTTON ===
    "string"
      ? process.env.REACT_APP_SHOW_COMPONENT_LAYOUT_DELETE_BUTTON === "true"
      : typeof process.env.REACT_APP_SHOW_COMPONENT_LAYOUT_DELETE_BUTTON ===
        "boolean"
      ? process.env.REACT_APP_SHOW_COMPONENT_LAYOUT_DELETE_BUTTON
      : false;

  const columns = [
    ...(showDeleteButton
      ? [
          {
            field: "delete",
            width: 90,
            headerName: `${t(messages.Delete())}`,
            filterable: false,
            renderCell: (props: GridRenderCellParams) => (
              <PermissionComponentWrapper
                requiredPermission={["edit"]}
                requiredPermissionGroup={["figmaComponentLayout"]}
              >
                <RenderDeleteButton
                  onClick={() =>
                    handleOnDelete?.(props.row as GfFlyerItemComponentPosition)
                  }
                  {...props}
                />
              </PermissionComponentWrapper>
            ),
            sortable: false,
          },
        ]
      : []),
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["figmaComponentLayout"]}
          >
            <RenderEditButton
              onClick={onEditTemplate}
              {...props}
            ></RenderEditButton>
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      field: "figmaTemplateData",
      width: 250,
      headerName: `Template`,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<GfFlyerItemComponentPosition>
      ) => {
        return <div>{params.row.figmaTemplateData?.name}</div>;
      },
    },
    {
      field: "store",
      width: 250,
      headerName: `${t(messages["Store"]())}`,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<GfFlyerItemComponentPosition>
      ) => {
        const store = params.row.store;
        const stores = params.row.figmaTemplateData?.stores || [];

        if (store) {
          return (
            <div>
              {store?.merchant?.name}
              {store?.signature && ` - ${store.signature}`}
            </div>
          );
        }

        return (
          <div>
            {stores.map((s, index) => (
              <div key={s.id || index}>
                {s?.merchant?.name}
                {s?.signature && ` - ${s.signature}`}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      field: "flyerItemCategory",
      width: 150,
      headerName: `${t(messages["Category"]())}`,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<GfFlyerItemComponentPosition>
      ) => {
        return (
          <div>
            {params.row.flyerItemCategory?.name ||
              (params.row?.frontPage ? FrontPage : "")}
          </div>
        );
      },
    },
    {
      field: "frontPage",
      type: "boolean",
      width: 100,
      headerName: `${t(messages["Front_Page"]())}`,
    },
    {
      field: "flyerItemCount",
      type: "number",
      width: 100,
      headerName: `${t(messages["Count"]())}`,
    },
    {
      field: "coordinates",
      width: 100,
      headerName: `${t(messages["Coordinates"]())}`,
      sortable: false,
      filterable: false,
      renderCell: (
        params: GridRenderCellParams<GfFlyerItemComponentPosition>
      ) => {
        const hasCoordinates = params.row.coordinates?.length > 0;
        return (
          <Button
            variant="text"
            size="small"
            style={{ textTransform: "capitalize" }}
            disabled={!hasCoordinates}
            data-testid="show-coordinates-button"
            onClick={(e) => {
              e.stopPropagation();
              onShowCoordinates?.(params.row);
            }}
          >
            {t(messages["Show"]())} ({params.row.coordinates?.length || 0})
          </Button>
        );
      },
    },

    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),
    {
      field: "id",
      width: 300,
      headerName: `${t(messages["id"]())}`,
      filterable: false,
    },
  ];

  return columns;
};
